/**
 * The learner's rank explained (D-165), on every exam's progress screen (TCF, TEF, IELTS, Goethe
 * level by level): the score out of 100 — the same as in the agency's activity reports —, its
 * three parts with their weight, the formula in plain words and the part to work on first.
 * Weights and windows come from the API (the seed), never from this file; only the learner's
 * own figures are shown.
 */

import { RANK_PARTS, formatRankPoints, parseRankExplained } from "@agencyos/core";
import { GlassCard } from "@agencyos/ui-web";
import { Calculator, Lightbulb } from "lucide-react";
import { useTranslation } from "react-i18next";

import { dateText } from "./shared";
import { CardTitle } from "./ui";

export function RankScoreCard({ rank, id = "academy-rank-score" }: { rank: unknown; id?: string }) {
  const { t, i18n } = useTranslation();
  const { formula, score, focus, computedAt } = parseRankExplained(rank);
  if (!formula) return null; // an older server: nothing to explain
  const days = { progressDays: formula.progressDays, activityDays: formula.activityDays };
  return (
    <GlassCard as="section" className="ac-card ac-rank-score" aria-labelledby={id}>
      <div className="ac-card__head">
        <CardTitle id={id} icon={<Calculator size={16} />} tone="info">
          {t("ranking.title")}
        </CardTitle>
        {score ? (
          <p className="ac-rank-score__total ao-num" aria-label={t("ranking.score", { score: formatRankPoints(score.total) })}>
            <strong>{formatRankPoints(score.total)}</strong>
            <span aria-hidden="true"> / 100</span>
          </p>
        ) : null}
      </div>
      {score ? (
        <ul className="ac-rank-score__parts">
          {RANK_PARTS.map((part) => {
            const label = t(`ranking.part.${part}`);
            const value = formatRankPoints(score.parts[part]);
            const weight = formula.weights[part];
            return (
              <li key={part} className={`ac-rank-score__part${focus === part ? " is-focus" : ""}`} aria-label={t("ranking.partAria", { label, value, weight })}>
                <div className="ac-rank-score__row" aria-hidden="true">
                  <span className="ac-rank-score__label">{label}</span>
                  <span className="ac-rank-score__weight">{t("ranking.partWeight", { weight })}</span>
                  <span className="ac-rank-score__value ao-num">{t("ranking.partValue", { value })}</span>
                </div>
                <span className="ac-progress" aria-hidden="true">
                  <span style={{ transform: `scaleX(${Math.min(Math.max(score.parts[part], 0), 100) / 100})` }} />
                </span>
                {part === "activity" ? (
                  <span className="ac-footnote">{t("ranking.activeDays", { count: score.activeDays, days: formula.activityDays })}</span>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="ac-footnote">{t("ranking.notRanked")}</p>
      )}
      <p className="ac-rank-score__explain">
        {t("ranking.explain", {
          level: formula.weights.level,
          progress: formula.weights.progress,
          activity: formula.weights.activity,
          ...days,
        })}
      </p>
      {score && focus ? (
        <p className="ac-rank-score__focus">
          <Lightbulb aria-hidden size={15} />
          <span>{t(`ranking.focus.${focus}`, days)}</span>
        </p>
      ) : null}
      {computedAt ? <p className="ac-footnote">{t("ranking.updated", { date: dateText(computedAt, i18n.language) })}</p> : null}
    </GlassCard>
  );
}
